import React, { useState, useRef, useEffect, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useToast } from './Toast';
import { Watermark, addWatermarkToImage, generatedImageDownloadFilename } from './Watermark';
import { RunningHubCosExpiryBadge } from './RunningHubCosExpiryBadge';
import { RUNNINGHUB_OUTPUT_MAX_AGE_MS, RUNNINGHUB_LOCAL_HISTORY_MAX_ITEMS } from '../utils/runningHubRetention';
import {
  createScopedRunningHubClientItemId,
  syncRunningHubHistoryItemToCloud,
  deleteRunningHubHistoryFromCloud,
} from '../utils/runningHubCloudHistory';
import { isRunningHubListThumbUrl } from '../utils/runningHubHistoryLocalStorage';
import {
  writeRunningHubHistoryListToLocalStorage,
  stampRunningHubHistoryOwner,
} from '../utils/runningHubHistoryScope';
import { RunningHubHistoryListThumb } from './RunningHubHistoryListThumb';
import { useRunningHubLinkIndicator } from '../utils/useRunningHubLinkIndicator';
import { useCreditDeduction } from '../hooks/useCreditDeduction';
import { useRunningHubPanelHistory } from '../hooks/useRunningHubPanelHistory';
import type { Lang } from '../utils/i18n';
import {
  startCompareSliderPointerDrag,
  startCompareSliderTouchDrag,
} from '../utils/compareSliderPointer';
import { attachComparePanelPinchZoom } from '../utils/comparePanelPinchZoom';
import { createRunningHubClientItemId, type RunningHubRecoveryRequest } from '../utils/applyRunningHubJobRecovery';
import { useRunningHubPanelRecovery } from '../hooks/useRunningHubPanelRecovery';
import { api } from '../services/apiClient';
import {
  getUnsupportedUploadImageFormatMessage,
  isSupportedUploadImageDataUrl,
  isSupportedUploadImageFile,
} from '../utils/supportedUploadImageFormat';

interface TextReconstructPanelProps {
  isOpen: boolean;
  onClose: () => void;
  sourceImage: string | null;
  isDarkMode: boolean;
  language: Lang;
  userEmail?: string | null;
  isAuthenticated?: boolean;
  recoveryRequest?: RunningHubRecoveryRequest | null;
}

/** 文本重构：RunningHub（.env RUNNINGHUB_TEXT_RECONSTRUCT_*，webApp 尾号 …7938） */
const TextReconstructPanel: React.FC<TextReconstructPanelProps> = ({
  isOpen,
  onClose,
  sourceImage,
  isDarkMode,
  language,
  userEmail,
  isAuthenticated = false,
  recoveryRequest = null,
}) => {
  const { showToast } = useToast();
  
  // 积分扣费 Hook
  const { deduct, isDeducting: isDeductingCredits } = useCreditDeduction({
    language: language === 'en' ? 'en' : 'zh',
    onInsufficientBalance: (required, available) => {
      showToast(
        language === 'en' 
          ? `Insufficient credits: need ${required}, have ${available}. Please recharge.` 
          : `积分不足：需要 ${required} 积分，当前 ${available} 积分，请及时充值`,
        'warning'
      );
    },
    onDeductionError: (error) => {
      showToast(
        language === 'en' ? `Credit deduction failed: ${error}` : `扣费失败：${error}`,
        'error'
      );
    }
  });

  const [isProcessing, setIsProcessing] = useState(false);
  const generationInFlightRef = useRef(false);
  const [progressDesc, setProgressDesc] = useState('');
  const [progressPercent, setProgressPercent] = useState(0);
  const [resultUrl, setResultUrl] = useState<string | null>(null);
  const [currentSourceImage, setCurrentSourceImage] = useState<string | null>(null);
  const [sliderPosition, setSliderPosition] = useState(50);
  const [isDragging, setIsDragging] = useState(false);
  const [zoomLevel, setZoomLevel] = useState(1);
  const [panX, setPanX] = useState(0);
  const [panY, setPanY] = useState(0);
  const [isPanning, setIsPanning] = useState(false);
  const [panStart, setPanStart] = useState({ x: 0, y: 0 });
  const [history, setHistory] = useState<
    Array<{ id: string; originalImage: string; upscaledUrl: string; modeLabel?: string; timestamp: number; thumbUrl?: string }>
  >([]);
  const [imageBounds, setImageBounds] = useState<{ left: number; width: number } | null>(null);
  const [selectedHistoryId, setSelectedHistoryId] = useState<string | null>(null);
  const runningHubLinked = useRunningHubLinkIndicator(isOpen);

  const containerRef = useRef<HTMLDivElement>(null);
  const imageContainerRef = useRef<HTMLDivElement>(null);
  const originalImageRef = useRef<HTMLImageElement>(null);
  const upscaledImageRef = useRef<HTMLImageElement>(null);
  const imageWrapperRef = useRef<HTMLDivElement>(null);
  const zoomLevelPinchRef = useRef(1);
  const sourceFileInputRef = useRef<HTMLInputElement>(null);
  const [sourceDropOver, setSourceDropOver] = useState(false);

  zoomLevelPinchRef.current = zoomLevel;

  const t = (en: string, zh: string) => (language === 'en' ? en : zh);
  const effectiveSourceImage = useMemo(() => currentSourceImage ?? sourceImage, [currentSourceImage, sourceImage]);
  const historyModeLabel = t('Text Reconstruction', '文本重构');

  const [textPrompt, setTextPrompt] = useState('');

  const getImageDimensions = (dataUrl: string): Promise<{ width: number; height: number }> =>
    new Promise((resolve) => {
      const img = new Image();
      img.onload = () => resolve({ width: img.naturalWidth || 1024, height: img.naturalHeight || 1024 });
      img.onerror = () => resolve({ width: 1024, height: 1024 });
      img.src = dataUrl;
    });

  const cleanExpired = (
    items: Array<{ id: string; originalImage: string; upscaledUrl: string; modeLabel?: string; timestamp: number }>,
  ) => {
    const now = Date.now();
    const filtered = items.filter((item) => now - item.timestamp < RUNNINGHUB_OUTPUT_MAX_AGE_MS);
    return filtered.slice(0, RUNNINGHUB_LOCAL_HISTORY_MAX_ITEMS);
  };

  useRunningHubPanelHistory({
    featureType: 'text_reconstruct',
    isOpen,
    isAuthenticated: !!isAuthenticated,
    userEmail,
    language,
    showToast,
    setHistory,
    cleanExpired,
    onBeforeClear: () => {
      setSelectedHistoryId(null);
      setResultUrl(null);
    },
  });

  // 主界面「文字命令 / 渲染」会更新 project.renderedImage；RunningHub 任务进行中禁止同步，避免对比区左侧被换成命令结果、与右侧旧 resultUrl 错位。
  useEffect(() => {
    if (generationInFlightRef.current) return;
    setCurrentSourceImage(sourceImage ?? null);
  }, [sourceImage, isProcessing]);

  useEffect(() => {
    if (isOpen && generationInFlightRef.current) {
      setIsProcessing(true);
    }
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) {
      if (!generationInFlightRef.current) {
        setResultUrl(null);
        setSliderPosition(50);
        setProgressDesc('');
        setProgressPercent(0);
        setZoomLevel(1);
        setPanX(0);
        setPanY(0);
        setSelectedHistoryId(null);
        setTextPrompt('');
      }
      setCurrentSourceImage(sourceImage ?? null);
    }
  }, [isOpen, sourceImage]);

  const readFileAsDataUrl = (file: File) =>
    new Promise<string>((resolve, reject) => {
      const r = new FileReader();
      r.onload = () => resolve(typeof r.result === 'string' ? r.result : '');
      r.onerror = () => reject(r.error ?? new Error('read failed'));
      r.readAsDataURL(file);
    });

  const applyLocalSourceFile = async (file: File) => {
    if (!isSupportedUploadImageFile(file)) {
      showToast(getUnsupportedUploadImageFormatMessage(language === 'en' ? 'en' : 'zh'), 'warning');
      return;
    }
    try {
      const dataUrl = await readFileAsDataUrl(file);
      setCurrentSourceImage(dataUrl || null);
      setResultUrl(null);
      setSelectedHistoryId(null);
      setSliderPosition(50);
      setZoomLevel(1);
      setPanX(0);
      setPanY(0);
    } catch {
      showToast(t('Failed to read image', '读取图片失败'), 'error');
    }
  };

  const appendTextReconstructHistory = (
    originalImage: string,
    resultUrls: string[],
    modeLabel: string,
    opts?: { skipCloudSync?: boolean; clientItemId?: string },
  ) => {
    const baseTs = Date.now();
    const cid = opts?.clientItemId?.trim();
    const newItems = resultUrls
      .map((upscaledUrl, i) =>
        stampRunningHubHistoryOwner(
          {
            id: cid
              ? i === 0
                ? cid
                : `${cid}-${i}`
              : createScopedRunningHubClientItemId('text_reconstruct', userEmail, !!isAuthenticated),
            originalImage,
            upscaledUrl,
            modeLabel,
            timestamp: baseTs + i,
          },
          userEmail,
        ),
      )
      .filter((it): it is NonNullable<typeof it> => it !== null);
    if (!newItems.length) return;
    setHistory((prev) => {
      const cleaned = cleanExpired(prev);
      const updated = [...newItems, ...cleaned].slice(0, RUNNINGHUB_LOCAL_HISTORY_MAX_ITEMS);
      writeRunningHubHistoryListToLocalStorage('text_reconstruct', userEmail, updated as unknown as Record<string, unknown>[]);
      if (!opts?.skipCloudSync) {
        void (async () => {
          let lastErr: string | undefined;
          const idToThumb = new Map<string, string>();
          for (const it of newItems) {
            const r = await syncRunningHubHistoryItemToCloud('text_reconstruct', !!isAuthenticated, it.id, it, {
              userEmailForLog: userEmail,
              currentUserEmail: userEmail,
            });
            if (!r.ok && !r.skipped) lastErr = r.error;
            if (r.thumbUrl && isRunningHubListThumbUrl(r.thumbUrl)) idToThumb.set(it.id, r.thumbUrl);
          }
          if (lastErr) {
            showToast(
              language === 'en' ? `Cloud archive failed: ${lastErr}` : `云端存档失败：${lastErr}`,
              'error',
            );
          }
          if (idToThumb.size) {
            setHistory((cur) => {
              const mapped = cur.map((x) => (idToThumb.has(x.id) ? { ...x, thumbUrl: idToThumb.get(x.id) } : x));
              writeRunningHubHistoryListToLocalStorage('text_reconstruct', userEmail, mapped as unknown as Record<string, unknown>[]);
              return mapped;
            });
          }
        })();
      }
      return updated;
    });
  };

  const handleGenerate = async () => {
    if (generationInFlightRef.current) return;
    if (!effectiveSourceImage) {
      showToast(t('No image available', '没有可用的图片'), 'warning');
      return;
    }
    if (!isSupportedUploadImageDataUrl(effectiveSourceImage)) {
      showToast(getUnsupportedUploadImageFormatMessage(language === 'en' ? 'en' : 'zh'), 'warning');
      return;
    }
    const trimmed = textPrompt.trim();
    if (!trimmed) {
      showToast(t('Please enter text in the text box.', '请在文本框中输入文字。'), 'warning');
      return;
    }

    generationInFlightRef.current = true;
    setIsProcessing(true);
    try {
      const deductResult = await deduct('text_reconstruct', t('Text Reconstruction', '文本重构'));
      if (!deductResult?.success) {
        return;
      }

      setResultUrl(null);
      setProgressPercent(12);
      setProgressDesc(t('Submitting text reconstruction...', '正在提交文本重构任务...'));
      try {
        const dimensions = await getImageDimensions(effectiveSourceImage);
        const clientItemId =
          isAuthenticated && userEmail?.trim()
            ? createRunningHubClientItemId('text_reconstruct', userEmail, true)
            : '';
        const jobMeta =
          clientItemId ?
            {
              clientItemId,
              historyPayloadDraft: {
                id: clientItemId,
                originalImage: effectiveSourceImage,
                timestamp: Date.now(),
                modeLabel: historyModeLabel,
                textPrompt: trimmed,
              },
            }
          : undefined;
        const resp = await api.submitTextReconstructRunningHub(
          effectiveSourceImage,
          trimmed,
          dimensions,
          userEmail?.trim() || undefined,
          jobMeta,
        );
        const urls: string[] = Array.isArray((resp as any)?.urls) ? (resp as any).urls : [];
        if (!urls.length) throw new Error(t('No image returned', '未返回图片结果'));
        setProgressPercent(100);
        setProgressDesc(t('Generation completed', '生成完成'));
        setResultUrl(urls[0]);
        appendTextReconstructHistory(effectiveSourceImage, urls, historyModeLabel, {
          skipCloudSync: !!(resp as any)?.historyArchived,
          clientItemId: clientItemId || undefined,
        });
        showToast(t('Generation completed', '生成完成'), 'success');
      } catch (error: any) {
        setProgressPercent(0);
        setProgressDesc(error?.message || t('Generation failed', '生成失败'));
        showToast(error?.message || t('Generation failed', '生成失败'), 'error');
      }
    } finally {
      generationInFlightRef.current = false;
      setIsProcessing(false);
    }
  };

  const handleDownload = async () => {
    if (!resultUrl) return;
    try {
      const wm = await addWatermarkToImage(resultUrl);
      const link = document.createElement('a');
      link.href = wm;
      link.download = generatedImageDownloadFilename(Date.now());
      link.click();
    } catch {
      showToast(
        t(
          'Download failed. Sign in and retry if the image is from cloud storage.',
          '下载失败，请登录账号后重试（云端图片需后端拉取才能加水印）。',
        ),
        'error',
      );
    }
  };

  const handleSelectHistoryItem = (item: (typeof history)[0]) => {
    setSelectedHistoryId(item.id);
    setResultUrl(item.upscaledUrl);
    setCurrentSourceImage(item.originalImage);
    setSliderPosition(50);
    setZoomLevel(1);
    setPanX(0);
    setPanY(0);
  };

  useRunningHubPanelRecovery({
    featureType: 'text_reconstruct',
    recoveryRequest,
    mapPayloadToItem: (p) => {
      const upscaledUrl = typeof p.upscaledUrl === 'string' ? p.upscaledUrl : '';
      if (!upscaledUrl) return null;
      return {
        id: String(
          p.id ||
            createScopedRunningHubClientItemId('text_reconstruct', userEmail, !!isAuthenticated),
        ),
        originalImage: String(p.originalImage || ''),
        upscaledUrl,
        modeLabel: typeof p.modeLabel === 'string' ? p.modeLabel : historyModeLabel,
        timestamp: Number(p.timestamp || Date.now()),
        thumbUrl:
          typeof p.thumbUrl === 'string' && isRunningHubListThumbUrl(p.thumbUrl) ? p.thumbUrl : undefined,
      };
    },
    mergeHistory: (item) => {
      setHistory((prev) => {
        const cleaned = cleanExpired(prev);
        const updated = [item, ...cleaned.filter((x) => x.id !== item.id)].slice(
          0,
          RUNNINGHUB_LOCAL_HISTORY_MAX_ITEMS,
        );
        writeRunningHubHistoryListToLocalStorage('text_reconstruct', userEmail, updated as unknown as Record<string, unknown>[]);
        return updated;
      });
    },
    applyItem: handleSelectHistoryItem,
  });

  const handleDownloadHistoryItem = async (e: React.MouseEvent, item: (typeof history)[0]) => {
    e.stopPropagation();
    try {
      showToast(t('Adding watermark...', '正在添加水印...'), 'info');
      const watermarkedImage = await addWatermarkToImage(item.upscaledUrl);
      const link = document.createElement('a');
      link.href = watermarkedImage;
      link.download = generatedImageDownloadFilename(item.timestamp);
      link.style.display = 'none';
      document.body.appendChild(link);
      link.click();
      setTimeout(() => document.body.removeChild(link), 100);
      showToast(t('Download started', '下载已开始'), 'success');
    } catch {
      showToast(
        t(
          'Download failed. Sign in and retry if the image is from cloud storage.',
          '下载失败，请登录账号后重试（云端图片需后端拉取才能加水印）。',
        ),
        'error',
      );
    }
  };

  const handleDeleteHistoryItem = (e: React.MouseEvent, itemId: string) => {
    e.stopPropagation();
    if (!confirm(t('Delete this history entry?', '确定要删除这条历史记录吗？'))) return;
    void deleteRunningHubHistoryFromCloud('text_reconstruct', !!isAuthenticated, itemId);
    setHistory((prev) => {
      const updated = prev.filter((x) => x.id !== itemId);
      writeRunningHubHistoryListToLocalStorage('text_reconstruct', userEmail, updated as unknown as Record<string, unknown>[]);
      return updated;
    });
    setSelectedHistoryId((cur) => {
      if (cur !== itemId) return cur;
      setResultUrl(null);
      setCurrentSourceImage(sourceImage ?? null);
      return null;
    });
  };

  // 滚轮缩放
  useEffect(() => {
    if (!resultUrl || !imageContainerRef.current) return;
    const handleWheel = (e: WheelEvent) => {
      e.preventDefault();
      const delta = e.deltaY > 0 ? -0.1 : 0.1;
      const newZoom = Math.max(0.5, Math.min(5, zoomLevel + delta));
      setZoomLevel(newZoom);
      if (newZoom <= 1) {
        setPanX(0);
        setPanY(0);
      }
    };
    const container = imageContainerRef.current;
    container.addEventListener('wheel', handleWheel, { passive: false });
    return () => container.removeEventListener('wheel', handleWheel as any);
  }, [resultUrl, zoomLevel]);

  useEffect(() => {
    if (!resultUrl || !imageContainerRef.current) return;
    return attachComparePanelPinchZoom(imageContainerRef.current, {
      getZoom: () => zoomLevelPinchRef.current,
      setZoom: (z) => {
        zoomLevelPinchRef.current = z;
        setZoomLevel(z);
        if (z <= 1) {
          setPanX(0);
          setPanY(0);
        }
      },
      shouldIgnoreTouchTarget: (t) =>
        !!(t && typeof (t as HTMLElement).closest === 'function' && (t as HTMLElement).closest('.slider-handle')),
    });
  }, [resultUrl]);

  // 拖拽平移（与光影重构一致）
  useEffect(() => {
    if (!resultUrl || !imageContainerRef.current) return;
    const container = imageContainerRef.current;

    const onDown = (e: MouseEvent) => {
      const target = e.target as HTMLElement | null;
      if (target?.closest('.cursor-ew-resize') || target?.closest('.slider-handle')) return;
      if (zoomLevel <= 1) return;
      e.preventDefault();
      setIsPanning(true);
      setPanStart({ x: e.clientX - panX, y: e.clientY - panY });
    };
    const onMove = (e: MouseEvent) => {
      if (!isPanning) return;
      setPanX(e.clientX - panStart.x);
      setPanY(e.clientY - panStart.y);
    };
    const onUp = () => setIsPanning(false);
    container.addEventListener('mousedown', onDown);
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => {
      container.removeEventListener('mousedown', onDown);
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
  }, [resultUrl, zoomLevel, isPanning, panX, panY, panStart]);

  if (!isOpen) return null;

  const panelBg = isDarkMode ? 'bg-black' : 'bg-white';
  const textPrimary = isDarkMode ? 'text-white' : 'text-zinc-900';
  const textFaint = isDarkMode ? 'text-white/50' : 'text-zinc-500';
  const cardBg = isDarkMode ? 'border-white/10 bg-white/5' : 'border-zinc-200 bg-zinc-50';

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          className="fixed inset-0 z-[2400] flex"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        >
          <div className="absolute inset-0 bg-black/80" onClick={onClose} />
          <motion.div
            className={`relative w-full h-full ${panelBg}`}
            initial={{ x: 40, opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            exit={{ x: 40, opacity: 0 }}
            transition={{ duration: 0.2 }}
          >
            {/* 顶栏 */}
            <div className="absolute left-0 top-0 right-0 h-16 flex items-center justify-between px-6 border-b border-white/10">
              <div className="flex items-center gap-3">
                <h2 className={`text-[18px] font-black tracking-tight ${textPrimary}`}>
                  {t('TEXT RECONSTRUCTION', '文本重构')}
                </h2>
                <div className="w-2 h-2 rounded-full" style={{ background: runningHubLinked ? '#22c55e' : '#6b7280' }} />
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={handleDownload}
                  disabled={!resultUrl}
                  className="px-4 py-2 bg-[#D7CDA1] text-[#101820] font-bold text-xs tracking-wider uppercase hover:bg-[#D7CDA1] disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                  style={{ borderRadius: '5px' }}
                >
                  {t('Download', '下载')}
                </button>
                <button
                  onClick={onClose}
                  className={`p-2 transition-all ${
                    isDarkMode ? 'bg-white/10 hover:bg-white/20 text-white' : 'bg-zinc-200 hover:bg-zinc-300 text-zinc-900'
                  }`}
                  style={{ borderRadius: '5px' }}
                  aria-label={t('Close', '关闭')}
                >
                  <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>

            {/* 左侧控制面板 */}
            <div
              ref={containerRef}
              className="absolute left-0 top-16 bottom-0 w-80 border-r border-white/10 px-6 py-6 overflow-y-auto"
            >
              <div className="mb-6">
                <h3 className={`text-[10px] font-semibold uppercase tracking-wider mb-3 ${textFaint}`}>
                  {t('INPUT SOURCE', '输入源')}
                </h3>
                <input
                  ref={sourceFileInputRef}
                  type="file"
                  accept="image/jpeg,image/jpg,image/png,image/webp,.jpg,.jpeg,.png,.webp"
                  className="hidden"
                  onChange={(e) => {
                    const f = e.target.files?.[0];
                    e.target.value = '';
                    if (f) void applyLocalSourceFile(f);
                  }}
                />
                <div
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      sourceFileInputRef.current?.click();
                    }
                  }}
                  className={`aspect-[3/4] overflow-hidden border flex flex-col items-center justify-center cursor-pointer transition-colors relative group ${
                    sourceDropOver ? 'border-[#D7CDA1] border-2 ring-2 ring-[#D7CDA1]/40' : ''
                  } ${cardBg}`}
                  style={{ borderRadius: '10px' }}
                  onClick={() => sourceFileInputRef.current?.click()}
                  onDragOver={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                  }}
                  onDragEnter={(e) => {
                    e.preventDefault();
                    setSourceDropOver(true);
                  }}
                  onDragLeave={(e) => {
                    const related = e.relatedTarget as Node | null;
                    if (related && (e.currentTarget as HTMLElement).contains(related)) return;
                    setSourceDropOver(false);
                  }}
                  onDrop={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setSourceDropOver(false);
                    const f = Array.from(e.dataTransfer.files).find((x) => x.type.startsWith('image/'));
                    if (f) void applyLocalSourceFile(f);
                  }}
                >
                  {effectiveSourceImage ? (
                    <img
                      src={effectiveSourceImage}
                      alt="Source"
                      className="w-full h-full object-contain pointer-events-none"
                      draggable={false}
                    />
                  ) : (
                    <div className={`flex flex-col items-center justify-center gap-2 px-4 text-center ${textFaint}`}>
                      <span className="text-3xl leading-none text-white/30 group-hover:text-white/50">+</span>
                      <p className="text-[10px] leading-snug">{t('Click or drag an image here', '点击或拖入图片')}</p>
                    </div>
                  )}
                </div>
              </div>

              {/* 5 按钮布局：上2 / 中1 / 下2 */}
              <div className="space-y-3">
                {/* 生成按钮（大小与“物理照片/白天”一致） */}
                <button
                  onClick={handleGenerate}
                  disabled={isProcessing || isDeductingCredits || !effectiveSourceImage}
                  className="w-full py-5 bg-[#D7CDA1] text-[#101820] font-bold text-sm tracking-wider uppercase hover:bg-[#D7CDA1] active:scale-[0.97] transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                  style={{ borderRadius: '9px' }}
                >
                  {t('GENERATE', '生成')}
                </button>

                {/* 单一文字对话框：宽度=阴天+夜晚总和 */}
                <textarea
                  value={textPrompt}
                  onChange={(e) => setTextPrompt(e.target.value)}
                  placeholder={t('Type here...', '在这里输入文字...')}
                  className={`w-full text-[12px] rounded-lg border px-3 py-2 outline-none focus:ring-1 focus:ring-[#D7CDA1] ${
                    isDarkMode
                      ? 'bg-black/30 border-white/10 text-white placeholder:text-white/30'
                      : 'bg-white border-zinc-200 text-zinc-900 placeholder:text-zinc-400'
                  }`}
                  style={{ minHeight: '216px', resize: 'vertical' }}
                />

              </div>

              {/* 说明文字 */}
              <div className={`text-[10px] leading-relaxed mt-4 font-normal ${textFaint}`}>
                <p>{t('Note: Generation may take several minutes. Please wait patiently.', '注意：生成可能需要几分钟时间，请耐心等待。')}</p>
                <p className="mt-2 text-[#D7CDA1]">
                  {t(
                    'AI generation may take multiple tries due to the algorithm. If you are not satisfied, click the button to generate again.',
                    'AI生成因算法原因，并非一次达到满意效果，如不满意，点击按钮继续生成。',
                  )}
                </p>
                <p className={`mt-2 text-[10px] font-normal ${isDarkMode ? 'text-orange-400' : 'text-orange-600'}`}>
                  {t(
                    'If the preview loads incorrectly, try switching to another image, then return here to check.',
                    '图片加载异常时，请尝试切换其他图片后再返回查看。',
                  )}
                </p>
              </div>

              {/* 进度条显示 */}
              {isProcessing && (
                <div className="mt-6">
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-[9px] text-white/60 font-semibold">{t('Progress', '进度')}</span>
                    <span className="text-[9px] text-[#D7CDA1] font-semibold">{Math.round(progressPercent)}%</span>
                  </div>
                  <div className="w-full h-2 bg-black/40 rounded-full overflow-hidden">
                    <div className="h-full bg-[#D7CDA1] transition-all duration-300 ease-out" style={{ width: `${progressPercent}%` }} />
                  </div>
                  <p className="text-[8px] text-white/50 mt-1 font-normal">{progressDesc}</p>
                </div>
              )}

              {/* 历史记录区域 */}
              <div className="mt-6 pt-6 border-t border-white/10">
                <h3 className="text-[10px] font-semibold uppercase tracking-wider text-white/50 mb-3">
                  {t('Text Reconstruction History', '文本重构历史')}
                </h3>
                {history.length === 0 ? (
                  <div className="text-[10px] text-white/50 text-center py-4 font-normal">
                    {t('No history yet', '暂无文本重构历史')}
                  </div>
                ) : (
                  <div className="space-y-3 max-h-[400px] overflow-y-auto">
                    {history.map((item) => {
                      const isSelected = selectedHistoryId === item.id;
                      return (
                        <div
                          key={item.id}
                          onClick={() => handleSelectHistoryItem(item)}
                          className={`overflow-hidden border bg-black/20 hover:border-[#D7CDA1]/50 cursor-pointer transition-all group relative ${
                            isSelected ? 'border-[#D7CDA1] border-2' : 'border-white/10'
                          }`}
                          style={{ borderRadius: '9px' }}
                        >
                          <div className="aspect-video relative bg-black/40">
                            <RunningHubHistoryListThumb
                              item={item}
                              imgClassName="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                            />
                            <div className="absolute top-2 left-2 z-[14] pointer-events-none">
                              <RunningHubCosExpiryBadge timestamp={item.timestamp} lang={language === 'en' ? 'en' : 'zh'} />
                            </div>
                            <div className="absolute top-2 right-2 bg-[#D7CDA1] text-[#101820] text-[8px] font-semibold px-2 py-1 rounded">
                              {item.modeLabel || t('Generated', '已生成')}
                            </div>
                            <div className="absolute bottom-1 right-1 z-[15] pointer-events-none">
                              <Watermark size="small" />
                            </div>
                            <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity flex items-end justify-center gap-2 p-2 z-[20]">
                              <button
                                type="button"
                                onClick={(e) => void handleDownloadHistoryItem(e, item)}
                                className="px-3 py-1.5 bg-[#D7CDA1] text-[#101820] text-[8px] font-semibold uppercase hover:bg-[#D7CDA1] transition-all"
                                style={{ borderRadius: '4px' }}
                              >
                                {t('Download', '下载')}
                              </button>
                              <button
                                type="button"
                                onClick={(e) => handleDeleteHistoryItem(e, item.id)}
                                className="px-3 py-1.5 bg-red-600 text-white text-[8px] font-semibold uppercase hover:brightness-110 transition-all"
                                style={{ borderRadius: '4px' }}
                              >
                                {t('Delete', '删除')}
                              </button>
                            </div>
                          </div>
                          <div className="px-2.5 py-2 border-t border-white/10 bg-black/30">
                            <div className="text-[9px] text-[#D7CDA1] font-semibold truncate">
                              {item.modeLabel || t('Generated', '已生成')}
                            </div>
                            <div className="text-[8px] text-white/50 mt-0.5">
                              {new Date(item.timestamp).toLocaleDateString()} •{' '}
                              {new Date(item.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>

            {/* 右侧对比区域（与光影重构一致） */}
            <div className={`absolute left-80 right-0 top-16 bottom-0 z-[2000] ${panelBg}`}>
              {resultUrl ? (
                <div
                  ref={imageContainerRef}
                  className={`relative w-full h-full overflow-hidden touch-none ${panelBg}`}
                  style={{ cursor: zoomLevel > 1 && isPanning ? 'grabbing' : zoomLevel > 1 ? 'grab' : 'default' }}
                >
                  <div
                    className="absolute inset-0 flex items-center justify-center touch-none"
                    style={{
                      transform: `scale(${zoomLevel}) translate(${panX / zoomLevel}px, ${panY / zoomLevel}px)`,
                      transformOrigin: 'center center',
                      transition: isDragging || isPanning ? 'none' : 'transform 0.1s ease-out',
                      width: '100%',
                      height: '100%',
                      overflow: 'visible',
                    }}
                  >
                    <div
                      ref={imageWrapperRef}
                      className="relative"
                      style={{
                        display: 'inline-block',
                        maxWidth: '100%',
                        maxHeight: '100vh',
                      }}
                    >
                      <img
                        ref={originalImageRef}
                        src={effectiveSourceImage || ''}
                        alt="Original"
                        className="select-none pointer-events-none"
                        style={{
                          display: 'block',
                          maxWidth: '100%',
                          maxHeight: 'calc(100vh - 64px)',
                          width: 'auto',
                          height: 'auto',
                          objectFit: 'contain',
                          verticalAlign: 'top',
                        }}
                        draggable={false}
                        onLoad={() => {
                          if (originalImageRef.current && upscaledImageRef.current && imageContainerRef.current) {
                            const original = originalImageRef.current;
                            const overlay = upscaledImageRef.current;
                            const w = original.offsetWidth;
                            const h = original.offsetHeight;
                            overlay.style.width = `${w}px`;
                            overlay.style.height = `${h}px`;
                            overlay.style.maxWidth = 'none';
                            overlay.style.maxHeight = 'none';
                            overlay.style.top = '0px';
                            overlay.style.left = '0px';
                            overlay.style.objectFit = 'contain';
                            requestAnimationFrame(() => {
                              if (originalImageRef.current && imageContainerRef.current) {
                                const imageRect = originalImageRef.current.getBoundingClientRect();
                                const containerRect = imageContainerRef.current.getBoundingClientRect();
                                setImageBounds({ left: imageRect.left - containerRect.left, width: imageRect.width });
                              }
                            });
                          }
                        }}
                      />
                      <img
                        ref={upscaledImageRef}
                        src={resultUrl}
                        alt="Result"
                        className="absolute select-none pointer-events-none"
                        style={{
                          position: 'absolute',
                          top: '0px',
                          left: '0px',
                          width: 'auto',
                          height: 'auto',
                          objectFit: 'contain',
                          clipPath: `inset(0 0 0 ${sliderPosition}%)`,
                          transition: isDragging ? 'none' : 'clip-path 0.05s linear',
                          display: 'block',
                          margin: '0',
                          padding: '0',
                          border: 'none',
                          outline: 'none',
                        }}
                        draggable={false}
                      />
                      <div className="absolute bottom-2 right-2 z-[15] pointer-events-none">
                        <Watermark size="small" />
                      </div>
                    </div>
                  </div>

                  <div
                    className="absolute top-0 bottom-0 z-[2200] flex justify-center cursor-ew-resize touch-none slider-handle"
                    style={{
                      left: imageBounds ? `${imageBounds.left + (sliderPosition / 100) * imageBounds.width}px` : '50%',
                      width: 44,
                      marginLeft: -22,
                      boxShadow: 'none',
                      transition: isDragging ? 'none' : 'left 0.05s linear',
                    }}
                    onPointerDown={(e) =>
                      startCompareSliderPointerDrag(e, {
                        originalImageRef,
                        imageContainerRef,
                        setSliderPercent: setSliderPosition,
                        setDragging: setIsDragging,
                        setImageBounds,
                      })
                    }
                    onTouchStart={(e) =>
                      startCompareSliderTouchDrag(e, {
                        originalImageRef,
                        imageContainerRef,
                        setSliderPercent: setSliderPosition,
                        setDragging: setIsDragging,
                        setImageBounds,
                      })
                    }
                  >
                    <div className="w-0.5 h-full shrink-0 bg-white/90 self-center pointer-events-none" />
                    <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-8 h-8 bg-white rounded-full shadow-lg flex items-center justify-center border-2 border-black pointer-events-none">
                      <svg className="w-4 h-4 text-black" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M8 9l4-4 4 4m0 6l-4 4-4-4" />
                      </svg>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="w-full h-full flex items-center justify-center opacity-60">
                  <div className="text-center">
                    <div className="w-16 h-16 mx-auto mb-3 rounded-full border border-white/10 flex items-center justify-center">
                      <span className="text-white/50 text-xl">T</span>
                    </div>
                    <div className="text-[11px] text-white/60 font-semibold tracking-wider">
                      {t('TEXT RECONSTRUCTION', '文本重构')}
                    </div>
                    <div className="text-[9px] text-white/40 mt-1">
                      {t('UI only (no backend).', '仅 UI 面板（未接入功能/接口）')}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default TextReconstructPanel;

